import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import type { DeclarationsCardsData } from "containers/DeclarationPage/utils";
import { toastApiError } from "helpers/apiErrorToast";
import { downloadFile, translationsWithFallback } from "helpers/utils";
import { t } from "i18next";
import { toast } from "react-toastify";

import type { TFetchStatus, TParamsPagination } from "./types";

export const fetchContributions = createAsyncThunk(
    "contribution/fetchContributions",
    async (_, { rejectWithValue }) => {
        try {
            const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/contributions`);
            return response.data.data;
        } catch (error: any) {
            console.log(error);
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchContribution = createAsyncThunk(
    "contribution/fetchContribution",
    async ({ id }: { id: number }, { rejectWithValue }) => {
        try {
            const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/contributions/${id}`);
            return response.data.data;
        } catch (error: any) {
            console.log(error);
            return rejectWithValue(error.response.data);
        }
    },
);

export const generateDeclaration = createAsyncThunk(
    "contribution/generateDeclaration",
    async ({
        declaration,
        format,
        mergeWithCompanyIds,
    }: {
        declaration: IDeclaration | DeclarationsCardsData;
        format?: "excel";
        // Admin-only. URSSAF declarations can be merged across a whitelisted group
        mergeWithCompanyIds?: number[];
    }) => {
        const loadingToast = toast.loading(`${t("Export.Pending")}`);

        try {
            const params: Record<string, string> = {};
            if (format) params.format = "excel";
            if (mergeWithCompanyIds && mergeWithCompanyIds.length > 0) {
                params.mergeWithCompanyIds = mergeWithCompanyIds.join(",");
            }
            const config = {
                params,
                headers: { Accept: "*/*" },
                responseType: "blob" as const,
            };

            const response = await axios.post(
                import.meta.env.REACT_APP_API_ENDPOINT + `/contributions/${declaration?.id}/generate`,
                {},
                config,
            );
            toast.dismiss(loadingToast);
            toast.success(`${t("Export.Successful")}`);

            let fileName =
                declaration.type === "das2"
                    ? t("Declarations.FileDownload.DAS2", { year: declaration.year - 1 })
                    : declaration.quarter > 0
                      ? t("Declarations.FileDownload.URSSAFQuarter", {
                            year: declaration.year,
                            quarter: declaration.quarter,
                        })
                      : t("Declarations.FileDownload.URSSAFYear", { year: declaration.year });
            if (format) {
                fileName = fileName.replace(".dat", ".xlsx");
            }
            downloadFile({ source: response.data, fileName });

            return response.data;
        } catch (error: any) {
            toast.dismiss(loadingToast);
            const errorMessage = JSON.parse(await error?.response?.data.text())?.message;
            toast.error(translationsWithFallback(errorMessage));
        }
    },
);

export const downloadDeclaration = createAsyncThunk(
    "contribution/downloadDeclaration",
    async ({ id }: { id: number }, { rejectWithValue }) => {
        try {
            const response = await toast.promise(
                axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/contributions/${id}/download`, {
                    responseType: "blob",
                }),
                {
                    pending: `${t("Declare.PreviewDownloadPending")}`,
                    success: `${t("Declare.PreviewDownloadSuccess")}`,
                    error: `${t("Declare.PreviewDownloadError")}`,
                },
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchAnalyseHistory = createAsyncThunk(
    "contribution/fetchAnalyseHistory",
    async (options: TParamsPagination, { rejectWithValue }) => {
        try {
            const { extraParams, ...rest } = options;
            const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/contributions/history`, {
                params: { ...rest, ...extraParams },
            });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchAnalyseHistoryDetails = createAsyncThunk(
    "contribution/fetchAnalyseHistoryDetails",
    async ({ uuid, dateFilters }: { uuid: string; dateFilters: any }, { rejectWithValue }) => {
        try {
            const response = await axios.get(
                import.meta.env.REACT_APP_API_ENDPOINT + `/contributions/history/${uuid}`,
                {
                    params: dateFilters,
                },
            );
            return response.data.data;
        } catch (error: any) {
            console.log(error);
            return rejectWithValue(error.response.data);
        }
    },
);

/**
 * Makes it possible to update declaration dates (declared or downloaded):
 */
export const updateDeclaration = createAsyncThunk(
    "contribution/updateDeclaration",
    async (
        { declarationId, data }: { declarationId: number; data: { downloadedAt?: Date; declaredAt?: Date } },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.put(
                import.meta.env.REACT_APP_API_ENDPOINT + `/contributions/${declarationId}`,
                data,
            );
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export type ArtworkNature =
    | "001"
    | "002"
    | "003"
    | "004"
    | "005"
    | "006"
    | "007"
    | "008"
    | "009"
    | "010"
    | "011"
    | "012"
    | "013"
    | "014"
    | "";

export type IncomeNature =
    | "001"
    | "002"
    | "003"
    | "004"
    | "005"
    | "006"
    | "007"
    | "008"
    | "009"
    | "010"
    | "011"
    | "012"
    | "013"
    | "014"
    | "015"
    | "016"
    | "";

export interface URSSAFDeclarationLine {
    id?: number;
    declarationId: number;
    companyId: number;
    socialProfileType: "1" | "2" | "3" | "4" | "5" | "6" | "7" | "8" | "9" | "L" | "A" | "B" | "C" | "D" | "H" | "I";
    socialSecurityNumber: string;
    lastname: string;
    firstname: string;
    streetNumber?: string | number;
    streetNumberMultiplierAdverb?: string;
    street: string;
    zipCode: string | number;
    city: string;
    country: string;
    birthDate: Date | string;
    artworkNature: ArtworkNature;
    incomeNature: IncomeNature;
    paymentDate: Date | string;
    siretNumber: string | number;
    grossDuty: number;
}

export interface DAS2DeclarationLine {
    id?: number;
    declarationId: number;
    companyId: number;
    lastname: string;
    firstname: string;
    activityType: string;
    vat: number;
    precount: number;
    streetNumber?: string;
    streetNumberMultiplierAdverb?: string;
    street?: string;
    zipCode?: string | number;
    city?: string;
    country?: string;
    birthDate: Date | string;
    paymentDate: Date | string;
    siretNumber: string;
    grossDuty: number;
    /**
     * Rémnuérations versées
     */
    rightsWithVat: number;
    /**
     * TVA nette sur droits d'auteur
     */
    rightsVat: number;
    /**
     * Taux de retenue fiscale
     */
    withheldForeign: number;
    /**
     * Taux retenue à la source
     */
    withheldPercent: number;
    extra: JSON;
}

export interface IDeclaration {
    id: number;
    companyId: number;
    startDate: Date | string;
    endDate: Date | string;
    quarter: 0 | 1 | 2 | 3 | 4;
    year: number;
    declaredAt: Date | string | null;
    lastRelanceAt: Date | string | null;
    downloadedAt: Date | string | null;
    lines?: URSSAFDeclarationLine[] | DAS2DeclarationLine[];
    type: "urssaf" | "das2";
}

export interface ContributionState {
    contribution: IDeclaration | null;
    contributionStatus: TFetchStatus;
    contributionFile: any | null;
    contributionList: IDeclaration[];
    contributionListStatus: TFetchStatus;
    analyseHistory: any[];
    analyseHistoryStatus: TFetchStatus;
    analyseHistoryDetails: any;
    analyseHistoryDetailsStatus: TFetchStatus;
    saveDeclarationStatus: TFetchStatus;
}

const initialState: ContributionState = {
    contribution: null,
    contributionStatus: "idle",
    contributionFile: null,
    contributionList: [],
    contributionListStatus: "idle",
    analyseHistory: [],
    analyseHistoryStatus: "idle",
    analyseHistoryDetails: null,
    analyseHistoryDetailsStatus: "idle",
    saveDeclarationStatus: "idle",
};

export const contributionSlice = createSlice({
    name: "contribution",
    initialState,
    reducers: {
        setContributionDeclared: (state, action) => {
            state.contributionList = state.contributionList.map((item) =>
                item.id == action.payload.contribution.id
                    ? {
                          ...action.payload.contribution,
                          declaredAt: action.payload.contribution.declaredAt || new Date().toISOString(),
                      }
                    : item,
            );
        },
    },
    extraReducers(builder) {
        builder.addCase(fetchContributions.fulfilled, (state, action) => {
            state.contributionList = action.payload;
            state.contributionListStatus = "succeeded";
        });
        builder.addCase(fetchContributions.pending, (state, action) => {
            state.contributionListStatus = "pending";
        });
        builder.addCase(fetchContributions.rejected, (state, { payload }) => {
            state.contributionListStatus = "failed";
            toastApiError(payload, "ServerError.InternalServerError");
        });

        builder.addCase(fetchContribution.fulfilled, (state, action) => {
            state.contribution = action.payload;
            state.contributionStatus = "succeeded";
        });
        builder.addCase(fetchContribution.pending, (state, action) => {
            state.contributionStatus = "pending";
        });
        builder.addCase(fetchContribution.rejected, (state, { payload }) => {
            state.contributionStatus = "failed";
            toastApiError(payload, "ServerError.InternalServerError");
        });

        builder.addCase(fetchAnalyseHistory.fulfilled, (state, action) => {
            if (action.payload.offset) state.analyseHistory = state.analyseHistory.concat(action.payload.entries);
            else state.analyseHistory = action.payload.entries;
            state.analyseHistoryStatus = "succeeded";
        });
        // using the pending function will make the page rerender every time the table on history analyse reachs the end
        // working solution would be to use another state to know if it's the request for first render
        // builder.addCase(fetchAnalyseHistory.pending, (state, action) => {
        //   state.analyseHistoryStatus = "pending";
        // });
        builder.addCase(fetchAnalyseHistory.rejected, (state, { payload }) => {
            state.analyseHistoryStatus = "failed";
            toastApiError(payload, "ServerError.InternalServerError");
        });

        builder.addCase(fetchAnalyseHistoryDetails.fulfilled, (state, action) => {
            state.analyseHistoryDetails = action.payload;
            state.analyseHistoryDetailsStatus = "succeeded";
        });
        builder.addCase(fetchAnalyseHistoryDetails.pending, (state, action) => {
            state.analyseHistoryDetailsStatus = "pending";
        });
        builder.addCase(fetchAnalyseHistoryDetails.rejected, (state, { payload }) => {
            state.analyseHistoryDetailsStatus = "failed";
            toastApiError(payload, "ServerError.InternalServerError");
        });
        builder.addCase(generateDeclaration.fulfilled, (state, action) => {
            state.contributionList = [...state.contributionList].map((item) => {
                if (item.id === action.meta.arg.declaration.id) {
                    return { ...item, downloadedAt: new Date() };
                } else {
                    return item;
                }
            });
        });
        builder.addCase(updateDeclaration.fulfilled, (state, { payload }) => {
            state.contributionList = [...state.contributionList].map((item) => {
                if (item.id === payload.id) {
                    return { ...payload };
                } else {
                    return item;
                }
            });
        });
    },
});

export const { setContributionDeclared } = contributionSlice.actions;
export default contributionSlice.reducer;
